/**
 * CAP-API-1a-site — #/site data: overview bundle + server group slots (DEF-FE-3b-001 partial).
 */

import { pickDevicesPollPath, pickOverviewPollPath } from "./device-presence.js";
import { fetchFleetDevicesList, fetchFleetDevicesPage, fetchFleetDevicesPoll } from "./fleet-list-paginated.js";
import { groupPageFleetCursorState } from "./group-page-fleet-load.js";
import {
  buildOverviewSlotsFromApiResponse,
  filterOverviewSlotsByOwner,
  hasUsableOverviewGroupSlots,
  overviewGroupSlotsTruncatedHtml,
  overviewTenantOwnerIndexTruncatedHtml,
} from "./overview-group-slots.js";
import { overviewFleetPollTruncatedHtml } from "./overview-fleet-poll.js";

/** First paint: one cursor page before explicit load-more (mirrors FE-G1-4 / #/devices). */
export const SITE_FLEET_INITIAL_PAGES = 1;

/**
 * @param {string} ownerFilterQ
 * @returns {boolean}
 */
export function siteOwnerServerFilterActive(ownerFilterQ) {
  const q = String(ownerFilterQ || "").trim();
  if (!q) return false;
  return q.length >= 2;
}

/**
 * @param {string} [bannerId]
 * @returns {string}
 */
export function siteOwnerServerFilterBannerHtml(bannerId = "siteOwnerServerFilterBanner") {
  return (
    `<p class="badge accent" id="${bannerId}" data-site-owner-server-filter="1" style="margin:8px 0">` +
    `Owner filter is on — results come in pages. Use Load more for additional matches.` +
    `</p>`
  );
}

/**
 * @param {object[]} list
 * @param {string} ownerFilterQ
 * @returns {object[]}
 */
export function filterSiteDevicesByOwner(list, ownerFilterQ) {
  const arr = Array.isArray(list) ? list : [];
  const q = String(ownerFilterQ || "").trim().toLowerCase();
  if (!q) return arr;
  if (siteOwnerServerFilterActive(ownerFilterQ)) return arr;
  return arr.filter((d) => String(d.owner_admin || "").toLowerCase().includes(q));
}

/**
 * @param {object | null | undefined} ov
 * @param {object[]} devices
 * @param {string} ownerFilterQ
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string, groupCardMetaKey: (gk: string, tenant?: string) => string, buildGroupSlotsFromDeviceList: (devs: object[]) => object[] }} ctx
 */
export function collectSiteGroupSlots(ov, devices, ownerFilterQ, ctx) {
  const filteredDevs = filterSiteDevicesByOwner(devices, ownerFilterQ);
  const fromApi = buildOverviewSlotsFromApiResponse(ov, ctx);
  if (fromApi != null) {
    return filterOverviewSlotsByOwner(fromApi, ownerFilterQ);
  }
  return ctx.buildGroupSlotsFromDeviceList(filteredDevs);
}

/**
 * Merge owner_admin usernames from server group slots + capped fleet sample (DEF-FE-SITE-001).
 *
 * @param {object | null | undefined} ov
 * @param {object[]} fleetDevices
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string, groupCardMetaKey: (gk: string, tenant?: string) => string }} ctx
 * @returns {{ owners: string[], slotOwnerCount: number, fleetOwnerCount: number, usesServerSlots: boolean }}
 */
export function collectSiteOwnerUsernames(ov, fleetDevices, ctx) {
  const fleet = Array.isArray(fleetDevices) ? fleetDevices : [];
  const fromFleet = new Set(
    fleet.map((d) => String(d.owner_admin || "").trim()).filter(Boolean),
  );
  const fromIndex = new Set();
  if (ov && Array.isArray(ov.tenant_owner_index)) {
    for (const u of ov.tenant_owner_index) {
      const t = String(u || "").trim();
      if (t) fromIndex.add(t);
    }
  }
  const slotRows = buildOverviewSlotsFromApiResponse(ov, ctx);
  const fromSlots = new Set();
  if (slotRows) {
    for (const s of slotRows) {
      const t = String(s.tenantOwner || "").trim();
      if (t) fromSlots.add(t);
    }
  }
  const owners = [...new Set([...fromIndex, ...fromSlots, ...fromFleet])].sort();
  return {
    owners,
    slotOwnerCount: fromSlots.size,
    fleetOwnerCount: fromFleet.size,
    indexOwnerCount: fromIndex.size,
    usesServerSlots: fromSlots.size > 0,
    usesServerOwnerIndex: fromIndex.size > 0,
  };
}

/**
 * @param {{ owners?: string[], slotOwnerCount?: number, fleetOwnerCount?: number, usesServerSlots?: boolean }} pack
 * @param {object | null | undefined} fleetList
 * @param {object | null | undefined} ov
 * @returns {string}
 */
export function siteOwnersIndexNote(pack, fleetList, ov) {
  const n = Array.isArray(pack && pack.owners) ? pack.owners.length : 0;
  const fleetTrunc = !!(fleetList && fleetList.truncated);
  const slotsTrunc = !!(ov && ov.notification_group_slots_truncated);
  const indexTrunc = !!(ov && ov.tenant_owner_index_truncated);
  if (pack && pack.usesServerOwnerIndex) {
    let line = `${n} account owners in directory`;
    if (pack.indexOwnerCount != null && Number(pack.indexOwnerCount) !== n) {
      line += ` (${pack.indexOwnerCount} indexed)`;
    }
    if (pack.usesServerSlots) line += ` · ${pack.slotOwnerCount} with groups`;
    if (fleetTrunc && pack.fleetOwnerCount) line += ` · fleet sample ${pack.fleetOwnerCount}`;
    if (indexTrunc) line += ". Directory list may be capped.";
    else if (fleetTrunc) line += ". Device table may still be incomplete.";
    return line;
  }
  if (pack && pack.usesServerSlots && fleetTrunc) {
    return `${n} account owners — from groups (${pack.slotOwnerCount}) merged with fleet sample (${pack.fleetOwnerCount}). Device table may be incomplete.`;
  }
  if (pack && pack.usesServerSlots) {
    let line = `${n} account owners — from groups and devices.`;
    if (slotsTrunc) line += " Group directory may be capped.";
    return line;
  }
  if (fleetTrunc) {
    return `${n} owners from a partial fleet sample — open Overview groups for the full owner list.`;
  }
  return `${n} unique account owner(s) from the loaded fleet.`;
}

/**
 * @param {object | null | undefined} list
 * @param {object | null | undefined} ov
 * @returns {string}
 */
export function siteFleetBannerHtml(list, ov) {
  const fleet = overviewFleetPollTruncatedHtml(list, ov, "siteFleetTruncBanner");
  const slots = overviewGroupSlotsTruncatedHtml(ov, "siteGroupSlotsTruncBanner");
  const owners = overviewTenantOwnerIndexTruncatedHtml(ov, "siteTenantOwnerIndexTruncBanner");
  return `${fleet}${slots}${owners}`;
}

/**
 * @param {object} d
 * @param {(d: object) => { cls: string }} presenceFn
 * @param {(pres: object) => string} badgeFn
 * @param {(s: string) => string} escapeHtmlFn
 * @returns {string}
 */
export function buildSiteDeviceTableRowHtml(d, presenceFn, badgeFn, escapeHtmlFn) {
  const pres = presenceFn(d);
  const badgeTxt = badgeFn(pres);
  const did = encodeURIComponent(d.device_id);
  return `<tr><td class="mono"><a href="#/devices/${did}">${escapeHtmlFn(d.device_id)}</a></td><td class="mono">${escapeHtmlFn(d.owner_admin || "—")}</td><td>${escapeHtmlFn(d.notification_group || "—")}</td><td>${escapeHtmlFn(d.zone || "")}</td><td><span class="badge ${pres.cls}">${badgeTxt}</span></td></tr>`;
}

/**
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 * @param {{ cursor: string, live?: boolean, timeoutMs?: number, retries?: number }} opts
 */
export async function appendSiteFleetPage(apiFn, opts) {
  const cursor = String(opts.cursor || "").trim();
  if (!cursor) return { items: [], page: null, paginated: false };
  const searchQ = opts.searchQ != null
    ? String(opts.searchQ).trim()
    : String(opts.ownerFilterQ || "").trim();
  return fetchFleetDevicesPage({
    cursor,
    live: !!opts.live,
    timeoutMs: opts.timeoutMs,
    retries: opts.retries,
    searchQ: searchQ || null,
  });
}

/** @param {object | null | undefined} list @param {object | null | undefined} page */
export function siteFleetCursorState(list, page) {
  return groupPageFleetCursorState(list, page);
}

/**
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 * @param {{ timeoutMs?: number, retries?: number, maxFleetPages?: number, ownerFilterQ?: string }} [opts]
 */
export async function fetchSiteOverviewBundle(apiFn, opts = {}) {
  const timeoutMs = Number.isFinite(Number(opts.timeoutMs)) ? Number(opts.timeoutMs) : 22000;
  const retries = Number.isFinite(Number(opts.retries)) ? Number(opts.retries) : 2;
  const maxFleetPages = opts.maxFleetPages != null ? opts.maxFleetPages : SITE_FLEET_INITIAL_PAGES;
  const ownerQ = String(opts.ownerFilterQ || "").trim();
  let ov = null;
  try {
    const raw = await apiFn(pickOverviewPollPath(), { timeoutMs, retries });
    if (raw && typeof raw === "object") ov = raw;
  } catch (_) { /* overview optional for site device table */ }
  let list = null;
  const devPath = pickDevicesPollPath();
  const live = devPath.includes("skip_cache=1");
  try {
    if (siteOwnerServerFilterActive(ownerQ)) {
      list = await fetchFleetDevicesList({
        live,
        timeoutMs,
        retries,
        maxPages: maxFleetPages,
        searchQ: ownerQ,
      });
      if (list && typeof list === "object") list.server_owner_filter = true;
    } else {
      list = await fetchFleetDevicesPoll({ live, timeoutMs, retries, maxPages: maxFleetPages });
    }
  } catch (_) { /* caller shows error */ }
  return { ov, list };
}

export { hasUsableOverviewGroupSlots };
